// src/features/developer/timestamp-converter/TimestampConverter.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, TextField, Typography, Grid, Autocomplete, Tooltip, IconButton, Paper } from '@mui/material';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {getUnixTime, getTime, fromUnixTime} from 'date-fns';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import ToolSection from '../components/ToolSection';
import StyledFAQ from '../../common/components/StyledFAQ';
import { timezones } from './data/timezones';
import {
    timestampToDate,
    getFormattedDateInTz,
    secondsToDuration,
    calculateDateBoundaries,
    BoundaryResult,
    DATE_FORMAT,
    UTC_TIMEZONE,
} from '../utils/timeUtils';


const TimestampConverter: React.FC = () => {
    const { t } = useTranslation();

    // --- State Management ---
    const [currentTime, setCurrentTime] = useState(new Date());
    const [copied, setCopied] = useState<string | null>(null);

    // State for Timestamp -> Date
    const [inputTimestamp, setInputTimestamp] = useState<string>('');
    const [selectedTz1, setSelectedTz1] = useState<string>(Intl.DateTimeFormat().resolvedOptions().timeZone);

    // State for Date -> Timestamp
    const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());

    // State for Seconds -> Duration
    const [inputSeconds, setInputSeconds] = useState<string>('');

    // State for Date Boundaries
    const [boundaryDate, setBoundaryDate] = useState<Date | null>(new Date());
    const [selectedTz2, setSelectedTz2] = useState<string>(Intl.DateTimeFormat().resolvedOptions().timeZone);

    // --- Effects ---
    // Real-time clock effect
    useEffect(() => {
        const timerId = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timerId);
    }, []);

    // Tooltip timer for copy feedback
    useEffect(() => {
        if (copied) {
            const timer = setTimeout(() => setCopied(null), 1500);
            return () => clearTimeout(timer);
        }
    }, [copied]);

    // --- Memoized Computations ---
    const localTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

    const timestampToDateResult = useMemo(() => {
        const date = timestampToDate(inputTimestamp);
        if (!date) {
            return { gmt: t('developer_timestamp_invalid_timestamp', { ns: 'tools' }), local: '-', selected: '-' };
        }
        return {
            gmt: getFormattedDateInTz(date, UTC_TIMEZONE),
            local: getFormattedDateInTz(date, localTimezone),
            selected: getFormattedDateInTz(date, selectedTz1),
        };
    }, [inputTimestamp, localTimezone, selectedTz1, t]);

    const dateToTimestampResult = useMemo(() => {
        if (!selectedDate) {
            return { seconds: '-', milliseconds: '-' };
        }
        return {
            seconds: getUnixTime(selectedDate).toString(),
            milliseconds: getTime(selectedDate).toString(),
        };
    }, [selectedDate]);

    const durationResult = useMemo(() => {
        const duration = secondsToDuration(Number(inputSeconds));
        if (!duration) return t('developer_timestamp_invalid_seconds', { ns: 'tools' });
        const { days, hours, minutes, seconds } = duration;
        return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    }, [inputSeconds, t]);

    const boundaryResult = useMemo((): (BoundaryResult & { error?: string }) | null => {
        if (!boundaryDate) return null;
        return calculateDateBoundaries(boundaryDate, selectedTz2);
    }, [boundaryDate, selectedTz2]);

    // --- Handlers ---
    const handleCopyToClipboard = (text: string, id: string) => {
        navigator.clipboard.writeText(text);
        setCopied(id);
    };

    // --- Render Helpers ---
    const renderResultRow = (label: string, value: string | number, copyId?: string) => (
        <Grid container alignItems="center" spacing={1} sx={{ py: 1 }}>
            <Grid item xs={12} sm={4}>
                <Typography variant="body1" color="text.secondary">{label}:</Typography>
            </Grid>
            <Grid item xs={11} sm={7}>
                <Paper variant="outlined" sx={{ p: '8px 12px', overflow: 'auto', bgcolor: 'action.hover' }}>
                    <Typography variant="body1" component="pre" sx={{ fontFamily: 'monospace' }}>
                        {value}
                    </Typography>
                </Paper>
            </Grid>
            {copyId && (
                <Grid item xs={1}>
                    <Tooltip title={copied === copyId ? t('developer_timestamp_copied_tooltip', { ns: 'tools' }) : t('developer_timestamp_copy_tooltip', { ns: 'tools' })}>
                        <IconButton size="small" onClick={() => handleCopyToClipboard(String(value), copyId)}>
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Grid>
            )}
        </Grid>
    );

    const faqs = [
        { q: 'developer_timestamp_faq1_q', a: 'developer_timestamp_faq1_a' },
        { q: 'developer_timestamp_faq2_q', a: 'developer_timestamp_faq2_a' },
        { q: 'developer_timestamp_faq3_q', a: 'developer_timestamp_faq3_a' },
    ];

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={4} alignItems="center">
                {/* Section 1: Current Time */}
                <ToolSection title={t('developer_timestamp_current_time_title', { ns: 'tools' })}>
                    {renderResultRow(t('developer_timestamp_current_timestamp_label', { ns: 'tools' }), getUnixTime(currentTime), 'current-ts')}
                    {renderResultRow(t('developer_timestamp_current_utc_time_label', { ns: 'tools' }), getFormattedDateInTz(currentTime, UTC_TIMEZONE), 'current-utc')}
                    {renderResultRow(t('developer_timestamp_current_local_time_label', { ns: 'tools' }), getFormattedDateInTz(currentTime, localTimezone), 'current-local')}
                </ToolSection>

                {/* Section 2: Timestamp to Date */}
                <ToolSection title={t('developer_timestamp_to_date_title', { ns: 'tools' })}>
                    <TextField
                        fullWidth
                        label={t('developer_timestamp_to_date_label', { ns: 'tools' })}
                        variant="outlined"
                        value={inputTimestamp}
                        onChange={(e) => setInputTimestamp(e.target.value)}
                        placeholder="e.g., 1672531200"
                    />
                    {renderResultRow(t('developer_timestamp_to_date_gmt_label', { ns: 'tools' }), timestampToDateResult.gmt)}
                    {renderResultRow(t('developer_timestamp_to_date_local_label', { ns: 'tools' }), timestampToDateResult.local)}
                    <Autocomplete
                        options={timezones}
                        value={selectedTz1}
                        onChange={(event, newValue) => { if (newValue) setSelectedTz1(newValue); }}
                        renderInput={(params) => <TextField {...params} label={t('developer_timestamp_timezone_selector_label', { ns: 'tools' })} />}
                        sx={{ mt: 1 }}
                    />
                    {renderResultRow(t('developer_timestamp_to_date_selected_tz_label', { ns: 'tools' }), timestampToDateResult.selected)}
                </ToolSection>

                {/* Section 3: Date to Timestamp */}
                <ToolSection title={t('developer_timestamp_from_date_title', { ns: 'tools' })}>
                    <DateTimePicker
                        label={t('developer_timestamp_from_date_picker_label', { ns: 'tools' })}
                        value={selectedDate}
                        onChange={(newValue) => setSelectedDate(newValue)}
                        renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                    {renderResultRow(t('developer_timestamp_from_date_seconds_label', { ns: 'tools' }), dateToTimestampResult.seconds, 'ts-seconds')}
                    {renderResultRow(t('developer_timestamp_from_date_ms_label', { ns: 'tools' }), dateToTimestampResult.milliseconds, 'ts-ms')}
                </ToolSection>

                {/* Section 4: Seconds -> Duration */}
                <ToolSection title={t('developer_timestamp_seconds_to_duration_title', { ns: 'tools' })}>
                    <TextField
                        fullWidth
                        label={t('developer_timestamp_seconds_to_duration_label', { ns: 'tools' })}
                        variant="outlined"
                        type="number"
                        value={inputSeconds}
                        onChange={(e) => setInputSeconds(e.target.value)}
                        placeholder="e.g., 86400"
                    />
                    {renderResultRow(t('developer_timestamp_seconds_to_duration_result', { ns: 'tools' }), durationResult)}
                </ToolSection>

                {/* Section 5: Date Boundaries */}
                <ToolSection title={t('developer_timestamp_date_boundaries_title', { ns: 'tools' })}>
                    <DateTimePicker
                        label={t('developer_timestamp_date_boundaries_picker_label', { ns: 'tools' })}
                        value={boundaryDate}
                        onChange={(newValue) => setBoundaryDate(newValue)}
                        renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                    <Autocomplete
                        options={timezones}
                        value={selectedTz2}
                        onChange={(event, newValue) => { if (newValue) setSelectedTz2(newValue); }}
                        renderInput={(params) => <TextField {...params} label={t('developer_timestamp_timezone_selector_label', { ns: 'tools' })} />}
                        sx={{ mt: 1 }}
                    />
                    {boundaryResult && (
                        <>
                            {renderResultRow(t('developer_timestamp_date_boundaries_day_start', { ns: 'tools' }), `${boundaryResult.startOfDayTs} (${getFormattedDateInTz(fromUnixTime(boundaryResult.startOfDayTs), selectedTz2)})`)}
                            {renderResultRow(t('developer_timestamp_date_boundaries_day_end', { ns: 'tools' }), `${boundaryResult.endOfDayTs} (${getFormattedDateInTz(fromUnixTime(boundaryResult.endOfDayTs), selectedTz2)})`)}
                            {renderResultRow(t('developer_timestamp_date_boundaries_month_start', { ns: 'tools' }), `${boundaryResult.startOfMonthTs} (${getFormattedDateInTz(fromUnixTime(boundaryResult.startOfMonthTs), selectedTz2)})`)}
                            {renderResultRow(t('developer_timestamp_date_boundaries_month_end', { ns: 'tools' }), `${boundaryResult.endOfMonthTs} (${getFormattedDateInTz(fromUnixTime(boundaryResult.endOfMonthTs), selectedTz2)})`)}
                            {renderResultRow(t('developer_timestamp_date_boundaries_year_start', { ns: 'tools' }), `${boundaryResult.startOfYearTs} (${getFormattedDateInTz(fromUnixTime(boundaryResult.startOfYearTs), selectedTz2)})`)}
                            {renderResultRow(t('developer_timestamp_date_boundaries_year_end', { ns: 'tools' }), `${boundaryResult.endOfYearTs} (${getFormattedDateInTz(fromUnixTime(boundaryResult.endOfYearTs), selectedTz2)})`)}
                        </>
                    )}
                </ToolSection>

                {/* Information Area */}
                <Box sx={{ my: 10, width: '100%' }}>
                    <Typography sx={{ mb: 4 }} variant="h4" component="h2" gutterBottom textAlign="center">
                        {t('developer_timestamp_how_to_title', { ns: 'tools' })}
                    </Typography>
                    <Typography sx={{ mb: 6 }} paragraph color="text.secondary" textAlign="center" maxWidth="800px" mx="auto">
                        {t('developer_timestamp_how_to_desc', { ns: 'tools' })}
                    </Typography>
                    <StyledFAQ
                        title={t('developer_timestamp_faq_title', { ns: 'tools' })}
                        faqs={faqs}
                        t={t}
                    />
                </Box>
            </Stack>
        </LocalizationProvider>
    );
};

export default TimestampConverter;